import { useState, useEffect, useRef, useCallback } from 'react';
import { BASE_WS_URL } from '../config/config';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

export default function useMatch() {
  const [matchData, setMatchData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isMountedRef = useRef(true);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const closeSocket = useCallback((socket) => {
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    try {
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
    } catch (err) {
      // Ignore close errors from already-closed sockets.
    }
  }, []);

  const connect = useCallback(() => {
    if (!isMountedRef.current) return;

    const existingSocket = wsRef.current;
    if (
      existingSocket &&
      (existingSocket.readyState === WebSocket.OPEN || existingSocket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const ws = new WebSocket(`${BASE_WS_URL}/sports`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (wsRef.current !== ws || !isMountedRef.current) return;
      reconnectAttempts.current = 0;
      setError(null);
      setLoading(false);
    };

    ws.onmessage = (event) => {
      if (wsRef.current !== ws || !isMountedRef.current) return;

      try {
        const data = JSON.parse(event.data);
        setMatchData(data);
        setLoading(false);
      } catch (err) {
        setError('Invalid match feed payload');
      }
    };

    ws.onerror = () => {
      if (wsRef.current !== ws || !isMountedRef.current) return;
      setError('Failed to connect to match feed');
      setLoading(false);
    };

    ws.onclose = () => {
      if (wsRef.current !== ws) return;

      wsRef.current = null;
      if (!isMountedRef.current) return;

      if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current += 1;
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connect();
          }
        }, RECONNECT_DELAY);
      } else {
        setError('Connection lost. Max reconnect attempts reached.');
      }
    };
  }, [clearReconnectTimer, isMountedRef]);

  useEffect(() => {
    isMountedRef.current = true;
    connect();

    return () => {
      isMountedRef.current = false;
      clearReconnectTimer();

      if (wsRef.current) {
        const socket = wsRef.current;
        wsRef.current = null;
        closeSocket(socket);
      }
    };
  }, [clearReconnectTimer, closeSocket, connect]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    clearReconnectTimer();

    if (wsRef.current) {
      closeSocket(wsRef.current);
      wsRef.current = null;
    }

    connect();
  }, [clearReconnectTimer, closeSocket, connect]);

  return { matchData, error, loading, reconnect };
}